import { memo } from "react";
import { ChevronDownIcon, ChevronLeftIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "../../lib/touchTarget";
import { Button } from "../ui/button";
import { COMPOSER_FOCUS_RING_CLASS } from "./composerFocusRing";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";

interface PendingActionState {
  readonly questionIndex: number;
  readonly isLastQuestion: boolean;
  readonly canAdvance: boolean;
  readonly isResponding: boolean;
  readonly isComplete: boolean;
}

interface ComposerPrimaryActionsProps {
  readonly compact: boolean;
  readonly sendVisualDensity?: "default" | "compact";
  readonly pendingAction: PendingActionState | null;
  readonly isRunning: boolean;
  readonly showPlanFollowUpPrompt: boolean;
  readonly promptHasText: boolean;
  readonly isSendBusy: boolean;
  /**
   * What the connect flow is doing while `isSendBusy`, shown in place of a
   * bare "Sending" (FOR-3579). Undefined/null keeps the existing label.
   */
  readonly sendingLabel?: string | null;
  readonly isConnecting: boolean;
  readonly isPreparingWorktree: boolean;
  readonly hasSendableContent: boolean;
  readonly isComposerDisabled: boolean;
  readonly onPreviousPendingQuestion: () => void;
  readonly onInterrupt: () => void;
  readonly onImplementPlanInNewThread: () => void;
}

function SendActionGlyph(props: { readonly busy: boolean }) {
  return props.busy ? (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      className="animate-spin"
      aria-hidden="true"
    >
      <circle
        cx="7"
        cy="7"
        r="5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="20 12"
      />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const formatPendingPrimaryActionLabel = (input: {
  compact: boolean;
  isLastQuestion: boolean;
  isResponding: boolean;
  questionIndex: number;
}) => {
  if (input.isResponding) {
    return "Submitting...";
  }
  if (input.compact) {
    return input.isLastQuestion ? "Submit" : "Next";
  }
  if (!input.isLastQuestion) {
    return "Next question";
  }
  return input.questionIndex > 0 ? "Submit answers" : "Submit answer";
};

export const ComposerPrimaryActions = memo(function ComposerPrimaryActions({
  compact,
  sendVisualDensity = "default",
  pendingAction,
  isRunning,
  showPlanFollowUpPrompt,
  promptHasText,
  isSendBusy,
  sendingLabel,
  isConnecting,
  isPreparingWorktree,
  hasSendableContent,
  isComposerDisabled,
  onPreviousPendingQuestion,
  onInterrupt,
  onImplementPlanInNewThread,
}: ComposerPrimaryActionsProps) {
  // Hoisted above the branches because a running turn can be interrupted from
  // any of them. t3code renders stop beside a pending question too: an agent
  // that asked something and kept working is exactly when a person wants out,
  // and the question can outlive the turn that raised it.
  const stopButton = isRunning ? (
    <button
      type="button"
      className={cn(
        RESPONSIVE_ICON_BUTTON_CLASS,
        COMPOSER_FOCUS_RING_CLASS,
        "cursor-pointer rounded-full bg-destructive/90 text-white transition-[background-color,transform] duration-150 hover:scale-105 hover:bg-destructive md:size-8 md:rounded-full",
      )}
      onClick={onInterrupt}
      aria-label="Stop generation"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
        <rect x="2" y="2" width="8" height="8" rx="1.5" />
      </svg>
    </button>
  ) : null;

  if (pendingAction) {
    return (
      <div className={cn("flex items-center justify-end", compact ? "gap-1.5" : "gap-2")}>
        {stopButton}
        {pendingAction.questionIndex > 0 ? (
          compact ? (
            <Button
              size="icon-sm"
              variant="outline"
              className={cn(RESPONSIVE_ICON_BUTTON_CLASS, "rounded-full md:size-7 md:rounded-full")}
              onClick={onPreviousPendingQuestion}
              disabled={pendingAction.isResponding}
              aria-label="Previous question"
            >
              <ChevronLeftIcon className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="min-h-touch touch-manipulation rounded-full sm:min-h-touch md:min-h-0"
              onClick={onPreviousPendingQuestion}
              disabled={pendingAction.isResponding}
            >
              Previous
            </Button>
          )
        ) : null}
        <Button
          type="submit"
          size="sm"
          className={cn(
            "min-h-touch touch-manipulation rounded-full sm:min-h-touch md:min-h-0",
            compact ? "px-3" : "px-4",
          )}
          disabled={
            pendingAction.isResponding ||
            (pendingAction.isLastQuestion ? !pendingAction.isComplete : !pendingAction.canAdvance)
          }
        >
          {formatPendingPrimaryActionLabel({
            compact,
            isLastQuestion: pendingAction.isLastQuestion,
            isResponding: pendingAction.isResponding,
            questionIndex: pendingAction.questionIndex,
          })}
        </Button>
      </div>
    );
  }

  if (isRunning) {
    // Send sits beside stop, after it, the way t3code orders the pair: stop is
    // the action a running turn is most likely to want, so it keeps the
    // position the eye already went to.
    //
    // Eligibility is `hasSendableContent`, not "the prompt has text". A Draft
    // holding only images is sendable, and gating on text alone left that Draft
    // with no way out while a turn ran.
    //
    // Double-submit is not this button's job. `useChatTurnSubmission`'s onSend
    // returns early on a `sendInFlightRef` it sets synchronously before its
    // first await, so a second click during the same turn is dropped there
    // whatever the button looks like. `isSendBusy` on the disabled expression
    // is the visible half of that.
    if (!hasSendableContent) {
      return stopButton;
    }

    return (
      <div className={cn("flex items-center justify-end", compact ? "gap-1.5" : "gap-2")}>
        {stopButton}
        <Button
          type="submit"
          size="sm"
          className={cn(
            "h-touch touch-manipulation rounded-full sm:h-touch md:h-8",
            compact ? "px-3" : "px-4",
          )}
          disabled={isComposerDisabled || isSendBusy || isConnecting}
          aria-label="Steer message"
        >
          {isConnecting || isSendBusy ? "Sending..." : "Steer"}
        </Button>
      </div>
    );
  }

  if (showPlanFollowUpPrompt) {
    if (promptHasText) {
      return (
        <Button
          type="submit"
          size="sm"
          className={cn(
            "h-touch touch-manipulation rounded-full sm:h-touch md:h-8",
            compact ? "px-3" : "px-4",
          )}
          disabled={isComposerDisabled || isSendBusy || isConnecting}
        >
          {isConnecting || isSendBusy ? "Sending..." : "Refine"}
        </Button>
      );
    }

    return (
      <div data-chat-composer-implement-actions="true" className="flex items-center justify-end">
        <Button
          type="submit"
          size="sm"
          className="h-touch touch-manipulation rounded-l-full rounded-r-none px-4 sm:h-touch md:h-8"
          disabled={isComposerDisabled || isSendBusy || isConnecting}
        >
          {isConnecting || isSendBusy ? "Sending..." : "Implement"}
        </Button>
        <Menu>
          <MenuTrigger
            render={
              <Button
                size="sm"
                variant="default"
                className="h-touch touch-manipulation rounded-l-none rounded-r-full border-l-white/12 px-2 sm:h-touch md:h-8"
                aria-label="Implementation actions"
                disabled={isComposerDisabled || isSendBusy || isConnecting}
              />
            }
          >
            <ChevronDownIcon className="size-3.5" />
          </MenuTrigger>
          <MenuPopup align="end" side="top">
            <MenuItem
              disabled={isComposerDisabled || isSendBusy || isConnecting}
              onClick={() => void onImplementPlanInNewThread()}
            >
              Implement in a new thread
            </MenuItem>
          </MenuPopup>
        </Menu>
      </div>
    );
  }

  return (
    <button
      type="submit"
      className={cn(
        RESPONSIVE_ICON_BUTTON_CLASS,
        COMPOSER_FOCUS_RING_CLASS,
        sendVisualDensity === "compact"
          ? "group/send enabled:cursor-pointer rounded-full bg-transparent text-primary-foreground disabled:pointer-events-none disabled:opacity-30 md:size-8 md:rounded-full"
          : "enabled:cursor-pointer rounded-full bg-primary/90 text-primary-foreground transition-[background-color,opacity,transform] duration-150 hover:scale-105 hover:bg-primary disabled:pointer-events-none disabled:opacity-30 disabled:hover:scale-100 md:size-8 md:rounded-full",
      )}
      disabled={isComposerDisabled || isSendBusy || isConnecting || !hasSendableContent}
      aria-label={
        isConnecting
          ? "Connecting"
          : isPreparingWorktree
            ? "Preparing worktree"
            : isSendBusy
              ? (sendingLabel ?? "Sending")
              : "Send message"
      }
    >
      {sendVisualDensity === "compact" ? (
        <span className="inline-flex size-9 items-center justify-center rounded-full bg-primary/90 transition-[background-color,transform] duration-150 group-hover/send:scale-105 group-hover/send:bg-primary group-disabled/send:scale-100 md:size-8">
          <SendActionGlyph busy={isConnecting || isSendBusy} />
        </span>
      ) : (
        <SendActionGlyph busy={isConnecting || isSendBusy} />
      )}
    </button>
  );
});
